import * as React from 'react'
import { ReactElement, useContext, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
    CommandBar,
    getTheme,
    ICommandBarItemProps,
    Icon,
    Image,
    mergeStyleSets,
    Panel,
    Persona,
    PersonaSize,
    PrimaryButton,
    SharedColors,
    Stack,
    StackItem,
} from '@fluentui/react'
import { Store } from '../reducers'
import MessageContext from '../context/MessageContext'
import { useHistory } from 'react-router-dom'
import { isAppContext } from '../helpers/appHelper'
import { AppContext } from '../config/appConfig'
import useUser from '../hooks/useUser'
import useUsers from '../hooks/useUsers'
import { ModuleType } from '../context/GlobalContext'
import useModules from '../hooks/module/useModules'
import useRoles, { Role, rolesTasks, rolesTimes } from '../hooks/useRoles'
import { Identity } from '../context/IdentityContext'
import useSharePoint from '../hooks/useSharePoint'
import { Link, tokens } from '@fluentui/react-components'
import { useSettings } from '../hooks/useSettings'
import { useTranslation } from 'react-i18next'
import { Timers } from './Timer/Timers'

const theme = getTheme()

type MenuItem = ICommandBarItemProps & {
    filter?: (item: MenuItem) => boolean
}

export enum MenuItemKey {
    Dashboard = 'dashboard',
    Files = 'files',
    Parties = 'parties',
    Emails = 'emails',
    Documents = 'documents',
    Tasks = 'tasks',
    Times = 'times',
    Finance = 'finance',
    SharePoint = 'sharepoint',
}

const largeClassNames = mergeStyleSets({
    icon: {
        fontSize: 20,
    },
    flexContainer: {
        minWidth: 40,
    },
})

const classNames = mergeStyleSets({
    commandBar: {
        padding: 0,
    },
    flexContainer: {
        // The absolutely positioned status icons might jump around otherwise.
        position: 'initial !important',
    },
    selected: {
        background: theme.palette.themePrimary,
        selectors: {
            // Underline selected command bar item. Styles are directly copied from selected Pivot items.
            ':before': {
                backgroundColor: 'transparent',
                bottom: 0,
                content: '""',
                height: 2,
                left: 8,
                position: 'absolute',
                right: 8,
                boxSizing: 'borderBox',
                transition: 'background-color 0.267s cubic-bezier(0.1, 0.25, 0.75, 0.9) 0s',
                borderBottom: '2px solid',
            },
        },
    },
    selectedHover: {
        selectors: {
            ':before': {
                borderBottomWidth: '2px',
                borderColor: theme.palette.themePrimary,
            },
        },
    },
    selectedIcon: {
        color: theme.palette.white,
        fontSize: 24,
    },
    statusIconContainer: { position: 'absolute', top: 0 },
    statusIcon: { color: SharedColors.red10 },
    sharePointLink: {
        width: '40px',
        height: '44px',
        paddingLeft: tokens.spacingHorizontalXS,
        paddingRight: tokens.spacingHorizontalXS,
    },
    sharePointIcon: {
        width: '24px',
        height: '24px',
        marginTop: tokens.spacingHorizontalXXS,
        padding: tokens.spacingHorizontalS,
    },
})

export default function Menu(): ReactElement {
    const { t: translate } = useTranslation()
    const settings = useSettings()
    const currentUser = useUser()
    const roles = useRoles()
    const users = useUsers()
    const modules = useModules()
    const { sharePoint } = useSharePoint()
    const { message, fileIdMap } = useContext(MessageContext)

    const breadcrumbs = useSelector<Store, string[]>(state => state.app.breadcrumbs)
    const history = useHistory()
    const [isHamburgerOpen, toggleHamburger] = useState<boolean>(false)

    // noinspection JSUnusedGlobalSymbols
    const items = useMemo<ICommandBarItemProps[]>(
        () => [
            ...[
                {
                    key: MenuItemKey.Dashboard,
                    iconProps: { iconName: 'Home' },
                    title: translate('menu.dashboard'),
                    route: '/',
                    filter: () => settings?.dashboard?.enabled !== false,
                },
                {
                    key: MenuItemKey.Files,
                    iconProps: { iconName: 'Album' },
                    title: translate('menu.file'),
                    route: '/file',
                },
                {
                    key: MenuItemKey.Parties,
                    iconProps: { iconName: 'Family' },
                    title: translate('menu.party'),
                    route: '/party',
                },
                {
                    key: MenuItemKey.Emails,
                    iconProps: { iconName: 'Mail' },
                    title: translate('menu.email'),
                    route: '/email',
                    filter: () => isAppContext(AppContext.Read) || isAppContext(AppContext.None),
                    onRenderIcon: (props, defaultRender) => (
                        <Stack horizontalAlign="center">
                            {defaultRender(props)}
                            {message.loaded && !fileIdMap && (
                                <StackItem styles={{ root: classNames.statusIconContainer }}>
                                    <Icon iconName="LocationDot" styles={{ root: classNames.statusIcon }} />
                                </StackItem>
                            )}
                        </Stack>
                    ),
                },
                {
                    key: MenuItemKey.Tasks,
                    iconProps: { iconName: 'TaskLogo' },
                    title: translate('menu.task'),
                    filter: () => modules.has(ModuleType.Tasks) && roles.any(rolesTasks),
                    route: '/task',
                },
                {
                    key: MenuItemKey.Times,
                    iconProps: { iconName: 'Timer' },
                    title: translate('menu.time'),
                    filter: () => modules.has(ModuleType.TimeTracking) && roles.any(rolesTimes),
                    route: '/time',
                },
                {
                    key: MenuItemKey.Finance,
                    iconProps: { iconName: 'Financial' },
                    title: translate('menu.finance'),
                    route: '/finance',
                    filter: () =>
                        (modules.has(ModuleType.Invoices) || modules.has(ModuleType.Reports)) &&
                        roles.any([Role.Invoice, Role.Report]),
                },
            ]
                .filter((item: MenuItem) => (item.filter ? item.filter(item) : true))
                .map(item => ({
                    ...item,
                    buttonStyles: {
                        ...largeClassNames,
                        flexContainer: classNames.flexContainer,
                        ...(breadcrumbs?.[0] === item.key && {
                            icon: classNames.selectedIcon,
                            root: classNames.selected,
                            rootHovered: classNames.selectedHover,
                            rootPressed: classNames.selectedHover,
                        }),
                    },
                    onClick: () => history.push(item.route),
                })),
            {
                key: 'sharepoint',
                onRender: () => (
                    <Link
                        href={sharePoint?.list?.webUrl}
                        target={'_blank'}
                        as={'a'}
                        className={classNames.sharePointLink}
                    >
                        <Image src={'./icons/sharepoint.svg'} className={classNames.sharePointIcon} />
                    </Link>
                ),
                buttonStyles: {
                    ...largeClassNames,
                },
            },
        ],
        [breadcrumbs, fileIdMap, message.data, roles.loaded, users.loaded, modules.loaded, sharePoint?.list?.webUrl],
    )

    return (
        <>
            <CommandBar
                styles={{ root: classNames.commandBar }}
                items={items}
                farItems={[
                    {
                        key: 'timers',
                        onRender: () => modules.has(ModuleType.TimeTracking) && roles.any(rolesTimes) && <Timers />,
                    },
                    {
                        key: 'hamburger',
                        iconProps: { iconName: 'CollapseMenu' },
                        onClick: () => {
                            toggleHamburger(!isHamburgerOpen)
                        },
                    },
                ]}
            />
            <Panel isOpen={isHamburgerOpen} onDismiss={() => toggleHamburger(false)}>
                <Persona text={currentUser?.fullName} key="current-persona" size={PersonaSize.size24} />
                <PrimaryButton
                    text={translate('menu.logout')}
                    iconProps={{ iconName: 'Leave' }}
                    onClick={() => Identity.current.logout()}
                />
            </Panel>
        </>
    )
}
